import React from "react";
import api from "../../Auth/Api";
import { Button, Typography, Tabs, Tab, Box, Chip, IconButton, Tooltip } from "@mui/material";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { PageContainer, SectionHeader, FormDialog, DataTable, formatDate, dur, preWrap } from "../../Layout/Common";
import PagedTable from "../../Layout/PagedTable";
import { subscribePoll } from "../../Layout/Poller";
import { AttachmentList } from "../../Layout/Attachments";

const PAGE = 15;
const TABS = [["queued", "Queued"], ["running", "Running"], ["done", "Done"]];
const PARTIAL_CUT = 2048, RESULT_CUT = 4096;   // long texts in the details dialog start collapsed beyond these sizes
const STATUS_COLOR = { queued: "default", running: "info", done: "success", failed: "error", timeout: "warning", canceled: "default" };
const TERMINAL = ["done", "failed", "timeout", "canceled"];
const seq3 = (n) => String(n ?? "").padStart(3, "0");
const active = (s) => s === "queued" || s === "running";
const kids = (b) => b.children || [];
const sizeOf = (b) => b.size || kids(b).length || 1;
const isMulti = (b) => sizeOf(b) > 1;
const csLabel = (cs) => /^cs:/i.test(String(cs)) ? String(cs) : `cs:${cs}`;
// Compact number for the join table: integers as-is, small decimals with 2 places, large values rounded.
const fmtNum = (v) => v == null || typeof v !== "number" || Number.isNaN(v) ? "—"
    : Number.isInteger(v) ? v.toLocaleString() : Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : v.toFixed(Math.abs(v) < 10 ? 2 : 1);

// Simulations: the async job queue served by the simulator nodes. Agents submit a manifest (`aidev sim-submit tests/sim.json`)
// which becomes one BATCH of N jobs (variants × shards); legacy single-script jobs are size-1 batches and render as plain rows.
// Rows are batches (`sim-batches`); a batch with several jobs expands into synthetic child rows (`_child`), one per variant.
// Details shows the batch header (summary, brief, bundle, numeric join from `sim-result`) and one tab per variant, each
// loading the full job (`sim-status`: files, partial results, result slice) on demand.
export default class SimulationsPage extends React.Component {
    state = { tab: "running", batches: [], total: 0, page: 1, now: Date.now(), expanded: {},
              details: null, variant: null, join: null, joinLoading: false, joinError: "", jobs: {}, showPartial: false, showResult: false };
    componentDidMount() { this.load(); this.unsub = subscribePoll(this.load, 10000); }
    componentDidUpdate() { this.syncClock(); }
    componentWillUnmount() { if (this.unsub) this.unsub(); this.stopClock(); }
    // The 1 Hz clock (live duration of running jobs) exists only while a running job or batch is on screen.
    syncClock = () => {
        const { batches, details } = this.state;
        const running = (b) => b.status === "running" || kids(b).some(c => c.status === "running");
        const need = batches.some(running) || (details && running(details));
        if (need && !this.clock) this.clock = setInterval(() => this.setState({ now: Date.now() }), 1000);
        else if (!need && this.clock) this.stopClock();
    };
    stopClock = () => { if (this.clock) { clearInterval(this.clock); this.clock = null; } };
    // A running job's duration is computed here from its start time (the backend only stores durationSec at completion).
    elapsed = (j) => j.status === "running" && j.startedDate ? Math.max(0, Math.floor((this.state.now - new Date(j.startedDate).getTime()) / 1000)) : (j.durationSec || 0);
    // Batch duration: a size-1 batch shows its only job's (as before); a larger one spans startedDate → finishedDate, live while running.
    batchElapsed = (b) => {
        const c = kids(b)[0];
        if (!isMulti(b) && c) return this.elapsed({ ...c, startedDate: c.startedDate || b.startedDate });
        if (b.status === "running" || !b.finishedDate || !b.startedDate) return this.elapsed(b);
        return b.durationSec || Math.max(0, Math.floor((new Date(b.finishedDate).getTime() - new Date(b.startedDate).getTime()) / 1000));
    };
    // Effective status of a batch row: size-1 batches keep their job's own status (e.g. "timeout", which the batch folds into "failed").
    batchStatus = (b) => (!isMulti(b) && kids(b)[0]?.status) || b.status;
    // Terminal counts: the server's `counts` when present, else derived from the inlined children.
    batchCounts = (b) => {
        const c = b.counts || {};
        const derived = Object.fromEntries(TERMINAL.map(k => [k, c[k] ?? kids(b).filter(x => x.status === k).length]));
        const terminal = b.terminalCount ?? TERMINAL.reduce((a, k) => a + (derived[k] || 0), 0);
        return { counts: derived, terminal, size: sizeOf(b) };
    };
    // Coalescing guard: a load requested while one runs is re-run once it ends (never dropped, never overlapped).
    load = async () => {
        if (this.loading) { this.pending = true; return true; }
        this.loading = true;
        try {
            const { tab, page } = this.state;
            const r = await api.development("Loading simulations", ["sim-batches"], { silent: true, payload: { status: tab, skip: (page - 1) * PAGE, limit: PAGE } });
            if (r && r.batches) this.setState(s => {
                // The open dialog's header follows the list (counts, status, summary); loaded per-job data is kept separately in `jobs`.
                const fresh = s.details && r.batches.find(b => b.id === s.details.id);
                return { batches: r.batches, total: r.total || 0, ...(fresh ? { details: { ...s.details, ...fresh } } : {}) };
            });
            return true;
        } catch { return false; }
        finally {
            this.loading = false;
            if (this.pending) { this.pending = false; this.load(); }
        }
    };
    setTab = (_, tab) => this.setState({ tab, page: 1, batches: [], total: 0 }, this.load);
    setPage = (p) => this.setState({ page: p }, this.load);
    // Expansion is keyed by batch id in state so polling (which replaces the rows) keeps it.
    toggle = (id) => this.setState(s => ({ expanded: { ...s.expanded, [id]: !s.expanded[id] } }));
    // `sim-cancel` accepts a batch id (whole study) or a job id (one variant); a size-1 batch cancels its job, as before.
    cancel = (id) => api.development("Cancelling simulation", ["sim-cancel"], { payload: { id } }).then(this.load).catch(() => {});
    cancelId = (b) => isMulti(b) ? b.id : (kids(b)[0]?.id || b.id);

    // Details: the batch row is the header at once; the numeric join (`sim-result`) and the selected variant's job (`sim-status`) load on demand.
    openDetails = (b, jobId) => {
        const variant = jobId || kids(b)[0]?.id || null;
        this.setState({ details: b, variant, join: null, joinLoading: false, joinError: "", jobs: {}, showPartial: false, showResult: false }, () => {
            if (isMulti(b)) this.loadJoin(b);
            if (variant) this.loadJob(variant);
        });
    };
    loadJoin = (b) => {
        this.setState({ joinLoading: true });
        api.development("Loading batch results", ["sim-result"], { silent: true, payload: { id: b.id } })
            .then(r => { if (this.state.details?.id === b.id) this.setState({ join: r || {}, joinLoading: false }); })
            .catch(e => { if (this.state.details?.id === b.id) this.setState({ joinLoading: false, joinError: e?.message || String(e) }); });
    };
    loadJob = (id) => {
        const cur = this.state.jobs[id];
        if (cur && (cur.loading || cur.data)) return;
        this.patchJob(id, { loading: true, error: "" });
        api.development("Loading simulation", ["sim-status"], { silent: true, payload: { id } })
            .then(r => this.patchJob(id, { loading: false, data: r && r.id ? r : null, error: r && r.id ? "" : "empty response" }))
            .catch(e => this.patchJob(id, { loading: false, error: e?.message || String(e) }));
    };
    patchJob = (id, patch) => this.setState(s => s.details ? { jobs: { ...s.jobs, [id]: { ...(s.jobs[id] || {}), ...patch } } } : null);
    selectVariant = (_, id) => this.setState({ variant: id, showPartial: false, showResult: false }, () => this.loadJob(id));
    closeDetails = () => this.setState({ details: null, variant: null, join: null, joinLoading: false, joinError: "", jobs: {} });
    // The job shown in a tab: the `sim-result` variant slice, then the full `sim-status` document, then the compact child row
    // (its non-null fields win: the list is polled, so status/fps/duration stay live while the loaded snapshot does not).
    jobView = (b, id) => {
        const child = kids(b).find(c => c.id === id) || { id };
        const fromResult = (this.state.join?.variants || []).find(v => v.id === id) || {};
        const st = this.state.jobs[id] || {};
        const live = Object.fromEntries(Object.entries(child).filter(([, v]) => v != null));
        return { ...fromResult, ...(st.data || {}), ...live, startedDate: child.startedDate || st.data?.startedDate || (isMulti(b) ? undefined : b.startedDate),
                 _loading: !!st.loading, _error: st.error || "" };
    };

    statusChip = (j) => <Chip size="small" color={STATUS_COLOR[j.status] || "default"} label={j.cancelRequested && j.status === "running" ? "cancelling…" : j.status} />;
    // Batch chip: a size-1 batch shows its job's plain status (as before); a larger one adds "<terminal>/<size>" and the other counts.
    batchChip = (b) => {
        const c = kids(b)[0];
        if (!isMulti(b)) return this.statusChip({ status: this.batchStatus(b), cancelRequested: c?.cancelRequested ?? b.cancelRequested });
        const { counts, terminal, size } = this.batchCounts(b);
        const hide = active(b.status) ? ["done"] : [b.status];
        const extras = TERMINAL.filter(k => !hide.includes(k) && counts[k] > 0).map(k => `${counts[k]} ${k}`);
        const head = b.cancelRequested && active(b.status) ? "cancelling…" : b.status;
        return <Chip size="small" color={STATUS_COLOR[b.status] || "default"} label={`${head} ${terminal}/${size}${extras.length ? ", " + extras.join(", ") : ""}`} />;
    };
    // Provenance of a bundle batch: short sha, workspace changeset, dirty flag; the full sha and entry on hover; links to the bundle when the url is known.
    provenanceChip = (b) => {
        if (!b.bundleSha) return null;
        const label = `bundle ${String(b.bundleSha).slice(0, 8)}${b.changeset ? ` · ${csLabel(b.changeset)}` : ""}${b.dirty ? " (dirty)" : ""}`;
        const link = b.bundleUrl ? { component: "a", href: b.bundleUrl, target: "_blank", rel: "noreferrer", clickable: true } : {};
        return <Tooltip title={`${b.bundleSha}${b.entry ? ` · entry ${b.entry}` : ""}`}><Chip size="small" variant="outlined" label={label} {...link} /></Tooltip>;
    };
    stats = (j) => { try { return JSON.parse(j.resourceStats || "{}"); } catch { return {}; } };
    fpsOf = (b) => {
        if (!isMulti(b)) return kids(b)[0]?.fps || b.fps || 0;
        const v = kids(b).map(c => c.fps).filter(f => f > 0);
        return v.length ? v.reduce((a, f) => a + f, 0) / v.length : 0;
    };
    filesOf = (b) => kids(b).length ? kids(b).reduce((a, c) => a + (c.fileCount || 0), 0) : (b.fileCount || 0);
    durationCell = (j, secs) => j.status === "queued" ? "—" : j.status === "running" && !j.startedDate && !j.durationSec ? "…" : dur(secs);
    // Long text block with a collapse threshold; `bytes` (when known) tells how much the server holds in total.
    textBlock = (title, text, cut, open, toggle, bytes) => {
        if (!text && !bytes) return null;
        const long = (text || "").length > cut;
        return (
            <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}{bytes ? ` (${Math.round(bytes / 1024)} KB)` : ""}</Typography>
                <pre style={{ ...preWrap, fontSize: 12, margin: 0, maxHeight: 360, overflow: "auto" }}>{long && !open ? text.slice(0, cut) + "…" : (text || "")}</pre>
                {long && <Button size="small" onClick={toggle}>{open ? "Show less" : `Show all (${Math.round(text.length / 1024)} KB)`}</Button>}
            </Box>
        );
    };

    // Table rows: batches, each expanded one followed by its children (`_child`, `_batch` points back to the parent).
    rows = () => {
        const { batches, expanded } = this.state, out = [];
        for (const b of batches) {
            out.push(b);
            if (isMulti(b) && expanded[b.id]) for (const c of kids(b)) out.push({ ...c, _child: true, _batch: b });
        }
        return out;
    };
    columns = (done) => [
        { id: "createdDate", label: "Submitted", sx: { whiteSpace: "nowrap" }, render: r => r._child ? "" : formatDate(r.createdDate) },
        { id: "submittedBy", label: "By", render: r => r._child ? "" : <>{r.submittedBy}{r.requestedBy ? <span style={{ color: "#888" }}> for {r.requestedBy}</span> : null}{r.priority > 0 ? <Chip size="small" color="warning" label="high" sx={{ ml: 0.5 }} /> : null}</> },
        {
            id: "name", label: "Simulation", render: r => r._child
                ? <Box sx={{ pl: 3.5, display: "flex", alignItems: "center", gap: 0.5 }}><span style={{ color: "#888" }}>↳</span>{r.variantId || r.id}</Box>
                : <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                    {isMulti(r) && <IconButton size="small" sx={{ p: 0.25 }} onClick={() => this.toggle(r.id)} title={this.state.expanded[r.id] ? "Collapse" : `Show ${sizeOf(r)} jobs`}>
                        {this.state.expanded[r.id] ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                    </IconButton>}
                    <span>{r.name || "(unnamed)"}</span>
                    {this.provenanceChip(r)}
                </Box>
        },
        { id: "status", label: "Status", render: r => r._child ? this.statusChip(r) : this.batchChip(r) },
        { id: "durationSec", label: "Duration", sx: { whiteSpace: "nowrap" }, render: r => r._child ? this.durationCell(r, this.elapsed(r)) : this.durationCell({ ...r, status: this.batchStatus(r) }, this.batchElapsed(r)) },
        { id: "fps", label: "FPS", render: r => { const f = r._child ? r.fps : this.fpsOf(r); return f ? <span title={!r._child && isMulti(r) ? "average over the batch's jobs" : undefined}>{f.toFixed(1)}</span> : "—"; } },
        { id: "summary", label: "Summary", render: r => r._child ? "" : (r.summary ? "✓" : (done ? "pending" : "")) },
        { id: "files", label: "Files", render: r => { const n = r._child ? r.fileCount : this.filesOf(r); return n ? n : ""; } },
        {
            id: "actions", label: "", width: "1%", sx: { whiteSpace: "nowrap" }, render: r => {
                const status = r._child ? r.status : this.batchStatus(r), cancelling = r.cancelRequested || (r._child && r._batch.cancelRequested && !TERMINAL.includes(r.status));
                return (
                    <>
                        {active(status) && !cancelling &&
                            <Button size="small" color="error" onClick={() => this.cancel(r._child ? r.id : this.cancelId(r))}>{!r._child && isMulti(r) ? "Cancel batch" : "Cancel"}</Button>}
                        <Button size="small" variant={done ? "contained" : "text"} sx={{ ml: 0.5 }} onClick={() => r._child ? this.openDetails(r._batch, r.id) : this.openDetails(r)}>Details</Button>
                    </>
                );
            }
        },
    ];

    // Numeric join (server-side): per variant, avg/min/max/sd/n of every top-level numeric field of the children's final summary.
    joinTable = (b) => {
        const j = this.state.join?.join || {};
        const order = [...new Set(kids(b).map(c => String(c.variantId || "").replace(/#\d+$/, "")))];
        const variants = Object.keys(j).sort((x, y) => (order.indexOf(x) + 1 || 1e9) - (order.indexOf(y) + 1 || 1e9));
        const fields = [...new Set(variants.flatMap(v => Object.keys(j[v] || {})))];
        if (!variants.length || !fields.length) return null;
        const cell = (c) => {
            if (typeof c === "number") return fmtNum(c);
            if (!c || c.avg == null) return "—";
            const tip = `avg ${fmtNum(c.avg)} · min ${fmtNum(c.min)} · max ${fmtNum(c.max)}${c.sd != null ? ` · sd ${fmtNum(c.sd)}` : ""}${c.n != null ? ` · n=${c.n}` : ""}`;
            return <Tooltip title={tip}><span>{fmtNum(c.avg)} <span style={{ color: "#888" }}>({fmtNum(c.min)}–{fmtNum(c.max)})</span></span></Tooltip>;
        };
        return (
            <DataTable rows={variants.map(v => ({ id: v, cells: j[v] || {} }))} getRowKey={r => r.id} sx={{ mb: 1.5 }} columns={[
                { id: "variant", label: "Variant", sx: { whiteSpace: "nowrap" }, render: r => r.id },
                ...fields.map(f => ({ id: f, label: f, align: "right", sx: { whiteSpace: "nowrap" }, render: r => cell(r.cells[f]) })),
            ]} />
        );
    };
    jobChips = (job) => {
        const st = this.stats(job);
        return (
            <>
                <Chip size="small" label={`duration ${dur(this.elapsed(job))} / max ${job.maxRuntimeSec ? dur(job.maxRuntimeSec) : "—"}`} color={job.status === "timeout" ? "warning" : "default"} />
                {job.fps ? <Chip size="small" label={`${job.fps.toFixed(1)} fps`} /> : null}
                {st.load1 != null && <Chip size="small" label={`load ${st.load1} · ${st.freeMemMB}/${st.totalMemMB} MB free`} />}
                {job._loading && <Chip size="small" variant="outlined" label="loading details…" />}
            </>
        );
    };
    // Per-job body (one variant): files, latest partial, result slice, runner error. `tabbed` adds the job's own chip line and caption.
    jobBody = (job, tabbed, batchId) => {
        const { showPartial, showResult } = this.state;
        const caption = [
            job.variantId ? `variant ${job.variantId}` : null,
            job.startedDate ? `started ${formatDate(job.startedDate)}` : null,
            job.finishedDate && !active(job.status) ? `finished ${formatDate(job.finishedDate)}` : null,
            job.id && job.id !== batchId ? `job ${job.id}` : null,
        ].filter(Boolean);
        return (
            <>
                {tabbed && <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>{this.statusChip(job)}{this.jobChips(job)}</Box>}
                {caption.length > 0 && <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>{caption.join(" · ")}</Typography>}
                {job._error && <Typography variant="caption" sx={{ display: "block", color: "error.main", mb: 1 }}>Could not load the full job: {job._error}</Typography>}
                {(job.files || []).length > 0 && <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2">Files uploaded by the simulation ({job.files.length})</Typography>
                    <AttachmentList attachments={job.files} height={96} caption={f => `${seq3(f.seq)} ${f.name || ""}${f.description ? ` — ${f.description}` : ""}${f.error ? ` (${f.error})` : ""}`} />
                </Box>}
                {this.textBlock(`Latest partial result${job.lastPartialAt ? ` (${formatDate(job.lastPartialAt)}${job.partialCount ? `, ${job.partialCount} relayed` : ""})` : ""}`,
                    job.lastPartial, PARTIAL_CUT, showPartial, () => this.setState({ showPartial: !showPartial }), null)}
                {this.textBlock("Result", job.result, RESULT_CUT, showResult, () => this.setState({ showResult: !showResult }), job.resultBytes)}
                {job.resultBytes > 0 && job.result && job.result.length < job.resultBytes && <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    Showing the first {Math.round(job.result.length / 1024)} KB of {Math.round(job.resultBytes / 1024)} KB — the submitting agent reads the whole result with `aidev sim-result`.
                </Typography>}
                {job.error && <Typography variant="caption" sx={{ ...preWrap, display: "block", mt: 1.5, color: "error.main" }}>Runner error: {job.error}</Typography>}
            </>
        );
    };

    render() {
        const { tab, total, page, details: d, variant, joinLoading, joinError } = this.state;
        const done = tab === "done";
        const multi = d ? isMulti(d) : false;
        const job = d && variant ? this.jobView(d, variant) : null;
        const { counts, terminal, size } = d ? this.batchCounts(d) : {};
        return (
            <PageContainer maxWidth={1100}>
                <SectionHeader title="Simulations">
                    <Button variant="outlined" onClick={this.load}>Refresh</Button>
                </SectionHeader>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                    Long simulations run on the shared simulator nodes; a study is one batch of jobs (variants × shards). The agent that requested it writes the summary you see here once it is notified of the result.
                </Typography>
                <Tabs value={tab} onChange={this.setTab} sx={{ mb: 1 }}>
                    {TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
                </Tabs>
                <PagedTable rows={this.rows()} total={total} page={page} pageSize={PAGE} onPage={this.setPage} getRowKey={r => r._child ? `c:${r.id}` : r.id} columns={this.columns(done)} />

                <FormDialog open={!!d} title={d ? `${multi ? "Simulation batch" : "Simulation"} — ${d.name || d.id}` : ""} onClose={this.closeDetails} cancelText="Close" hideConfirm maxWidth="md" fullWidth>
                    {d && <>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                            {this.batchChip(d)}
                            <Chip size="small" label={`by ${d.submittedBy}${d.requestedBy ? ` for ${d.requestedBy}` : ""}`} />
                            {d.priority > 0 ? <Chip size="small" color="warning" label="high priority" /> : null}
                            {this.provenanceChip(d)}
                            {multi
                                ? <Chip size="small" label={`${size} jobs · ${terminal} finished${TERMINAL.filter(k => counts[k] > 0).map(k => ` · ${counts[k]} ${k}`).join("")} · ${dur(this.batchElapsed(d))}`} />
                                : (job && this.jobChips(job))}
                            {joinLoading && <Chip size="small" variant="outlined" label="loading results…" />}
                        </Box>
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                            submitted {formatDate(d.createdDate)}{d.startedDate ? ` · started ${formatDate(d.startedDate)}` : ""}{d.finishedDate && !active(d.status) ? ` · finished ${formatDate(d.finishedDate)}` : ""}{d.taskId ? ` · task ${d.taskId}` : ""}{d.entry ? ` · entry ${d.entry}` : ""} · id {d.id}
                        </Typography>
                        {d.requestBrief && <><Typography variant="subtitle2" sx={{ mb: 0.5 }}>Request (why / what / how)</Typography>
                            <Typography variant="body2" sx={{ ...preWrap, mb: 1.5 }}>{d.requestBrief}</Typography></>}
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Summary {d.summaryBy ? `(by ${d.summaryBy}, ${formatDate(d.summarizedAt)})` : ""}</Typography>
                        {d.summary
                            ? <Typography variant="body2" sx={{ ...preWrap, mb: 1.5 }}>{d.summary}</Typography>
                            : <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                                No summary yet — {d.submittedBy} is notified when the {multi ? "batch" : "job"} finishes and writes it on its next cycle{d.requestedBy ? `; ${d.requestedBy} then receives it automatically` : ""}.
                              </Typography>}
                        {multi && <>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Numeric join <span style={{ fontWeight: "normal", color: "#888" }}>— per variant, avg (min–max) of the numeric fields in the jobs' final summaries; sd and n on hover</span></Typography>
                            {this.joinTable(d) || <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                                {joinError ? `Results not available: ${joinError}` : joinLoading ? "Loading…" : active(d.status) ? "Available once the jobs report their final summary." : "No numeric fields in the jobs' final summaries."}
                            </Typography>}
                            <Tabs value={kids(d).some(c => c.id === variant) ? variant : false} onChange={this.selectVariant} variant="scrollable" scrollButtons="auto" sx={{ mb: 1, borderBottom: 1, borderColor: "divider" }}>
                                {kids(d).map(c => <Tab key={c.id} value={c.id} sx={{ textTransform: "none", minHeight: 40 }}
                                    label={<span>{c.variantId || c.id}<span style={{ color: "#888" }}> · {c.cancelRequested && c.status === "running" ? "cancelling…" : c.status}</span></span>} />)}
                            </Tabs>
                        </>}
                        {job && this.jobBody(job, multi, d.id)}
                        {multi && job && active(job.status) && !job.cancelRequested && !d.cancelRequested &&
                            <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => this.cancel(job.id)}>Cancel this variant</Button>}
                    </>}
                </FormDialog>
            </PageContainer>
        );
    }
}
